import {
  Flex,
  HStack,
  ParameterTypeEnum,
  ParametersTable,
  Stack,
  Table,
  TableSize,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  setAlpha,
  useParameterRows,
  type ParameterRow,
} from '@talos/kyoko';
import { useMemo } from 'react';
import styled from 'styled-components';
import type { PreTradeResponseData } from '../../../redux/preTradeApiTypes';
import { isFieldConsidered } from '../../../utils';
import { EnabledBadge } from './EnabledBadge';
import { LABEL_COLOR_STYLE, NOT_CONSIDERED_LABEL_STYLE, NotConsideredLabelSuffix } from './NotConsideredLabel';
import { useStrategyOverviewRows } from './useStrategyOverviewRows';

interface StrategySummaryProps {
  scenario: PreTradeResponseData;
  showNonConsidered?: boolean;
}

const ENABLED_BADGE_PARAMETER_TYPES = new Set<string>([
  ParameterTypeEnum.Mode,
  ParameterTypeEnum.PriceProtection,
  ParameterTypeEnum.UnifiedLiquidity,
  ParameterTypeEnum.ReduceOnly,
  ParameterTypeEnum.ReduceFirst,
]);

/** Restricts Enabled/Disabled badges to explicit toggle parameter types only. */
export function shouldRenderEnabledBadge(row: ParameterRow): boolean {
  return row.isEnabled !== undefined && !!row.parameterType && ENABLED_BADGE_PARAMETER_TYPES.has(row.parameterType);
}

const ContentBlock = styled(Stack)`
  background: ${({ theme }) => setAlpha(0.04, theme.colors.gray['100'])};
  border-radius: 3px;
  padding: 12px;
  gap: 6px;
  flex: 1;
  justify-content: flex-start;
  align-items: stretch;
`;

export function StrategySummary({ scenario, showNonConsidered = false }: StrategySummaryProps) {
  const { NewOrderSingle, NOSConsideredFields } = scenario;
  const { Strategy, Parameters } = NewOrderSingle ?? {};

  const allOverviewRows = useStrategyOverviewRows(scenario);
  const allParameterRows = useParameterRows(Strategy, Parameters);

  const { overviewRows, parameterRows } = useMemo(() => {
    if (showNonConsidered) {
      return { overviewRows: allOverviewRows, parameterRows: allParameterRows };
    }
    return {
      overviewRows: allOverviewRows.filter(
        row => !row.consideredField || isFieldConsidered(NOSConsideredFields, row.consideredField)
      ),
      parameterRows: allParameterRows.filter(row => !row.name || isFieldConsidered(NOSConsideredFields, row.name)),
    };
  }, [allOverviewRows, allParameterRows, showNonConsidered, NOSConsideredFields]);

  return (
    <Flex gap="spacing12" w="100%" data-testid="strategy-summary">
      <ContentBlock>
        <Table size={TableSize.Small}>
          <Tbody>
            {overviewRows.map(row => {
              const isNotConsidered =
                !!row.consideredField && !isFieldConsidered(NOSConsideredFields, row.consideredField);

              return (
                <Tr key={row.name}>
                  <Th style={isNotConsidered ? NOT_CONSIDERED_LABEL_STYLE : undefined}>
                    <HStack gap="spacing4" alignItems="center" justifyContent="flex-start">
                      <Text color={row.labelColor ?? 'colorTextDefault'}>{row.label}</Text>
                      {isNotConsidered && <NotConsideredLabelSuffix />}
                    </HStack>
                  </Th>
                  <Td align="right">
                    <Flex justifyContent="flex-end">{row.value}</Flex>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </ContentBlock>

      <ContentBlock>
        <ParametersTable
          rows={parameterRows}
          size={TableSize.Small}
          decorateRow={row => {
            const isNotConsidered = !!row.name && !isFieldConsidered(NOSConsideredFields, row.name);
            const showEnabledBadge = shouldRenderEnabledBadge(row);

            return {
              value: showEnabledBadge ? <EnabledBadge enabled={row.isEnabled === true} /> : undefined,
              labelSuffix: isNotConsidered ? <NotConsideredLabelSuffix /> : undefined,
              labelStyle: isNotConsidered ? NOT_CONSIDERED_LABEL_STYLE : LABEL_COLOR_STYLE,
            };
          }}
        />
      </ContentBlock>
    </Flex>
  );
}
